import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaApple,
  FaAndroid,
  FaChevronLeft,
  FaChevronRight,
  FaFigma,
} from "react-icons/fa";
import ProgressiveImage from "../ProgressiveImage";

const detailPairs = [
  ["b1", "p1"],
  ["b2", "p2"],
  ["b3", "p3"],
  ["b4", "p4"],
];

export const ModalView = ({ isOpen, onClose, selectedItem }) => {
  const modalRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = selectedItem?.image || [];

  useEffect(() => {
    setCurrentIndex(0);
  }, [selectedItem]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target))
        onClose();
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !selectedItem) return null;

  const goToPrevious = () => {
    setCurrentIndex((index) => (index - 1 + images.length) % images.length);
  };
  const goToNext = () => {
    setCurrentIndex((index) => (index + 1) % images.length);
  };
  const details = detailPairs.filter(([heading]) => selectedItem[heading]);
  const featuredDetail = details[0];
  const remainingDetails = details.slice(1);
  const technologies = Array.isArray(selectedItem.technology)
    ? selectedItem.technology
    : selectedItem.technology
        ?.split(",")
        .map((item) => item.trim())
        .filter(Boolean) || [];
  const categories = Array.isArray(selectedItem.category)
    ? selectedItem.category
    : selectedItem.category
      ? [selectedItem.category]
      : [];
  const projectLinks = Array.isArray(selectedItem.link)
    ? selectedItem.link[0] || {}
    : {};
  const appStore = selectedItem.appStore || projectLinks.appstore;
  const playStore = selectedItem.playStore || projectLinks.playstore;
  const figma = selectedItem.figma || projectLinks.figma;
  const portalTarget = document.querySelector(".app-shell") || document.body;

  return createPortal(
    <div className="project-modal-backdrop" role="presentation">
      <div className="project-modal-shell" ref={modalRef}>
        <button
          className="project-modal-close"
          onClick={onClose}
          aria-label="Close project details"
        >
          <span>Close</span>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        <section
          className="project-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-modal-title"
        >
          <div className="project-modal-media">
            {images.length > 1 && (
              <div className="project-modal-kicker">
                <span>
                  {String(currentIndex + 1).padStart(2, "0")} /{" "}
                  {String(images.length).padStart(2, "0")}
                </span>
              </div>
            )}
            {images.length > 0 && (
              <div className="project-modal-visual">
                <ProgressiveImage
                  src={images[currentIndex]}
                  alt={selectedItem.title + " preview " + (currentIndex + 1)}
                  loading="eager"
                  decoding="sync"
                  fetchPriority="high"
                />
                {images.length > 1 && (
                  <div className="project-modal-controls">
                    <button
                      onClick={goToPrevious}
                      aria-label="Previous project image"
                    >
                      <FaChevronLeft />
                    </button>
                    <button onClick={goToNext} aria-label="Next project image">
                      <FaChevronRight />
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="project-modal-snapshot">
              <p className="project-modal-snapshot-label">Project snapshot</p>
              <div className="project-modal-snapshot-grid">
                <div>
                  <strong>{categories[0] || "Digital"}</strong>
                  <span>Platform</span>
                </div>
                <div>
                  <strong>{technologies.length}</strong>
                  <span>Technologies</span>
                </div>
                <div>
                  <strong>{String(images.length).padStart(2, "0")}</strong>
                  <span>Visuals</span>
                </div>
              </div>
              {featuredDetail && (
                <div className="project-modal-impact">
                  <span>{selectedItem[featuredDetail[0]]}</span>
                  {selectedItem[featuredDetail[1]] && (
                    <p>{selectedItem[featuredDetail[1]]}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="project-modal-content">
            <p className="project-modal-eyebrow">Project case study</p>
            <h2 id="project-modal-title">{selectedItem.title}</h2>
            <div className="project-modal-tech" aria-label="Technologies used">
              {technologies.map((technology) => (
                <span key={technology}>{technology}</span>
              ))}
            </div>
            {(appStore || playStore || figma) && (
              <div className="project-modal-links project-modal-links-top">
                {appStore && (
                  <a href={appStore} target="_blank" rel="noreferrer">
                    <FaApple /> App Store
                  </a>
                )}
                {playStore && (
                  <a href={playStore} target="_blank" rel="noreferrer">
                    <FaAndroid /> Google Play
                  </a>
                )}
                {figma && (
                  <a href={figma} target="_blank" rel="noreferrer">
                    <FaFigma /> View prototype
                  </a>
                )}
              </div>
            )}
            {selectedItem.description && (
              <p className="project-modal-description">
                {selectedItem.description}
              </p>
            )}
            {remainingDetails.length > 0 && (
              <div className="project-modal-details">
                {remainingDetails.map(([heading, copy]) => (
                  <article className="project-modal-detail" key={heading}>
                    <p>{selectedItem[heading]}</p>
                    {selectedItem[copy] && <span>{selectedItem[copy]}</span>}
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>,
    portalTarget,
  );
};
